import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { OwnedPartner, ownedPartnerKind } from '../models/partners.model';
import { WebTokenBridge } from '../web-auth/web-token.bridge';
import { WebSession, WebSessionRegistry } from '../web-auth/web-session.registry';
import { AccountType, clearPendingAccountType, tokenIdentities } from './account-type';
import { AuthService } from './auth.service';
import { CardV2Gate } from './card-v2-gate.service';
import { PartnersService } from './partners.service';

/**
 * Hesap modu: yönetilen partner (kurum) varsa "organizasyon", yoksa "kurumsal".
 * - Kart v2 bayrağı kapalıyken (prod derlemesi) HİÇ istek atmaz, her zaman kurumsaldır.
 * - Sonuç oturumdaki kullanıcıya (JWT `sub`) bağlıdır; kullanıcı değişince önceki sonuç
 *   kullanılmaz ve mod kurumsala döner (hesap değişiminde izolasyon).
 * - İlk girişte bekleyen "organizasyon" seçimi varsa ve Owned boşsa kurum açılır.
 * - Owned okunamazsa kurumsal kalınır (bilinçli fail-open: mevcut kurumsal akış bozulmasın),
 *   konsola yazılır ve aynı kullanıcı için sonraki çağrıda yeniden denenir.
 * - Ent web oturumunda (QR, scp=ent) Owned şirket hesabı adına okunur ve QR kurulumunun açtığı Şirket kaydını da
 *   içerir; bu yüzden satır türüne (kind) bakılır (panel QR F2/F9):
 *     yalnız Üyelik → "organizasyon" (Üyelik menüsü + Üyelik sayfasına yönlenme),
 *     Şirket + Üyelik → "kurumsal" kalır, Üyelik menüsü eklenmez,
 *     yalnız Şirket ya da boş → "kurumsal".
 *   Türü bilinmeyen satır (alan gelmeyen eski sunucu) ent'te Şirket sayılır: şirket hesabı dashboard'unu kaybetmez.
 *   Ent dışı (Keycloak) oturumda türü bilinmeyen satır eskisi gibi Üyelik sayılır; bilinen Şirket satırı sayılmaz.
 * - Org web oturumunda (QR /org girişi, scp=org; Keycloak/ent token'ı yok) mod her zaman "organizasyon"dur: Owned
 *   çağrılmaz (uç org token'ına kapalı) ve kart v2 bayrağına bakılmaz; oturum türü menünün türünü belirler.
 *   Önbellek anahtarı org:<kullanıcı>:<kurum> (kurum değişince yeniden çözülür).
 */
@Injectable({ providedIn: 'root' })
export class AccountModeService {
    // PartnersService (HttpClient) ilk çözümde alınır: menü/layout bu servisi enjekte ettiğinde
    // bayrak kapalıyken HTTP zinciri hiç kurulmaz.
    private readonly injector = inject(Injector);
    private readonly auth = inject(AuthService);
    private readonly gate = inject(CardV2Gate);

    private readonly modeSubject = new BehaviorSubject<AccountType>('corporate');
    readonly mode$: Observable<AccountType> = this.modeSubject.asObservable();

    private owned: OwnedPartner[] = [];
    private resolvedFor: string | null = null;
    private inflight: { uid: string; promise: Promise<AccountType> } | null = null;

    mode(): AccountType {
        return this.modeSubject.value;
    }

    isOrganization(): boolean {
        return this.modeSubject.value === 'organization';
    }

    /** Son çözümde okunan yönetilen kurumlar (yalnız aynı kullanıcı için geçerli). */
    ownedPartners(): OwnedPartner[] {
        return this.resolvedFor !== null && this.resolvedFor === this.sessionKey() ? this.owned : [];
    }

    /** Hesap modunu çözer; aynı kullanıcı için önbellekten döner. Hata atmaz. */
    resolve(): Promise<AccountType> {
        const org = this.orgSurface();
        if (org) return Promise.resolve(this.set(orgKey(org), [], 'organization'));
        if (!this.gate.flagOn()) return Promise.resolve(this.set(null, [], 'corporate'));
        const uid = this.sessionKey();
        if (!uid) return Promise.resolve(this.set(null, [], 'corporate'));
        if (this.resolvedFor === uid) return Promise.resolve(this.modeSubject.value);
        if (this.resolvedFor !== null) this.set(null, [], 'corporate'); // başka kullanıcı: eski sonuç düşer
        if (this.inflight?.uid === uid) return this.inflight.promise;
        const promise = this.load(uid).finally(() => {
            if (this.inflight?.promise === promise) this.inflight = null;
        });
        this.inflight = { uid, promise };
        return promise;
    }

    /** Önbelleği temizler (ör. kurum oluşturulduktan/silindikten sonra yeniden okumak için). */
    reset(): void {
        this.inflight = null;
        this.set(null, [], 'corporate');
    }

    private async load(uid: string): Promise<AccountType> {
        let owned: OwnedPartner[];
        try {
            owned = await firstValueFrom(this.partners().owned());
        } catch (e) {
            console.error('Account mode: owned partners could not be read; staying corporate', e);
            return this.modeSubject.value;
        }
        if (this.sessionKey() !== uid) return this.modeSubject.value; // arada hesap (ya da oturum türü) değişti
        if (owned.length === 0) {
            const identities = tokenIdentities(this.auth.getAccessToken());
            // K14: eski kayıt yolu kalktı; bekleyen türden kurum AÇILMAZ, yalnız eski anahtar temizlenir.
            // Owned boşsa kurulum yoktur, kullanıcı /setup'a QR ile gelir.
            clearPendingAccountType(identities);
        }
        const ent = this.isEnt();
        const isMembership = (p: OwnedPartner): boolean => {
            const kind = ownedPartnerKind(p);
            return kind === null ? !ent : kind === 'Membership';
        };
        const memberships = owned.filter(isMembership).length;
        if (memberships === 0) return this.set(uid, owned, 'corporate');
        // Ent'te Şirket kaydı da varsa kurumsal kalınır. Ent dışı eski davranış: organizasyon.
        const company = ent && memberships < owned.length;
        return this.set(uid, owned, company ? 'corporate' : 'organization');
    }

    /** Önbellek anahtarı: kullanıcı + oturum türü (ent'te türü bilinmeyen satır farklı yorumlanır). */
    private sessionKey(): string | null {
        const uid = this.auth.getUserId();
        if (!uid) {
            const org = this.orgSurface();
            return org ? orgKey(org) : null;
        }
        return this.isEnt() ? `ent:${uid}` : uid;
    }

    /** Yalnız org web oturumu (Keycloak/ent kimliği yok, org kaydı canlı) varsa o oturum; aksi halde null. */
    private orgSurface(): WebSession | null {
        if (this.auth.getUserId()) return null;
        return this.injector.get(WebSessionRegistry).current('org');
    }

    // Bayrak kapalıyken web oturumu kayıt defteri (HttpBackend) de kurulmaz; ilk çözümde alınır.
    private isEnt(): boolean {
        return this.injector.get(WebTokenBridge).isEnt();
    }

    private partners(): PartnersService {
        return this.injector.get(PartnersService);
    }

    private set(uid: string | null, owned: OwnedPartner[], mode: AccountType): AccountType {
        this.resolvedFor = uid;
        this.owned = owned;
        if (this.modeSubject.value !== mode) this.modeSubject.next(mode);
        return mode;
    }
}

function orgKey(s: WebSession): string {
    return `org:${s.userId}:${s.orgId ?? ''}`;
}
